import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { User } from 'lucide-react';
import UserManagementSummary from '../components/UserManagementSummary';
import UserManagementTable from '../components/UserManagementTable';
import { useClient } from '../ClientContext';
import { authApiClient } from '../hooks/useAuthInterceptor';

const styles = {
  container: { padding: '48px' },
  header: { display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' },
  subHeader: { mb: '32px' },
  title: { fontSize: '24px', fontWeight: 700 },
};

const UserManagementPage = ({ user, accessToken }) => {
  const navigate = useNavigate();
  const { selectedClient, config } = useClient();
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const usersByClientEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users`;
  const adminUserEndpoint = `${process.env.REACT_APP_BACKEND_URL}/admin/users-admin`;

  // Check if user management is allowed for the selected client
  const shouldShowUserManagement = useCallback(() => {
    if (selectedClient === 'All Dashboards') return true; // Always allowed for "All Dashboards"
    const clientMap = (config && config.clients) || {};
    const clientConfig = clientMap[selectedClient];
    return clientConfig?.showUserManagement !== false; // Default to true if not specified
  }, [selectedClient, config]);

  useEffect(() => {
    if (config && !shouldShowUserManagement()) {
      // Redirect to admin page if user management is not allowed for this client
      navigate('/admin', { replace: true });
    }
  }, [selectedClient, config, navigate, shouldShowUserManagement]);

  useEffect(() => {
    // Don't fetch data if user management is not allowed for this client
    if (!accessToken || (config && !shouldShowUserManagement())) {
      return;
    }
    const fetchData = async (endpoint, setDataCallback) => {
      try {
        const response = await authApiClient.get(endpoint.replace(process.env.REACT_APP_BACKEND_URL, ''));
        setDataCallback(response.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching data:', err);
        setError('Error fetching data.');
      }
    };

    const effectiveClient = selectedClient === 'All Dashboards' ? 'Tidal' : selectedClient;
    const userEndpoint = `${usersByClientEndpoint}?client=${encodeURIComponent(effectiveClient)}`;

    fetchData(userEndpoint, (data) => setUsers(data.users || []));
    fetchData(adminUserEndpoint, (data) => setAdmins(data.adminUsers || []));
  }, [
    selectedClient,
    usersByClientEndpoint,
    adminUserEndpoint,
    accessToken,
    config,
    shouldShowUserManagement,
  ]);

  // Don't render content if user management is not allowed
  if (config && !shouldShowUserManagement()) {
    return null; // Will redirect via useEffect
  }

  const countUsers = (users, admins) => {
    let externalUsers = 0;
    let beatdappUsers = 0;

    users.forEach((user) => {
      if (user.email.endsWith('@beatdapp.com')) {
        beatdappUsers++;
      } else {
        externalUsers++;
      }
    });

    return {
      totalUsers: [...users, ...admins].length,
      externalUsers,
      beatdappUsers,
      totalAdmins: admins.length,
    };
  };

  const userStats = countUsers(users, admins);

  if (error) return <div>{error}</div>;

  return (
    <Layout profilePic={user.picture}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <User size={24} />
          <Typography sx={styles.title}>User Management</Typography>
        </Box>
        <Box sx={styles.subHeader}>
          <Typography variant="body2" gutterBottom>
            Manage users and their permissions for the selected dashboard
          </Typography>
        </Box>
        <UserManagementSummary
          totalUsers={userStats.totalUsers}
          externalUsers={userStats.externalUsers}
          beatdappUsers={userStats.beatdappUsers}
          totalAdmins={userStats.totalAdmins}
        />
        <UserManagementTable accessToken={accessToken} />
      </Box>
    </Layout>
  );
};

export default UserManagementPage;
